import {
  getRequestGeneric,
  postRequestGeneric,
} from "@/services/backendApiGeneric";
import { getRequest, postRequest, putRequest, type ErrorHandler } from "@/services/backendApi";

// ═══════════════════════════════════════════════════════════════════
// Types
// ═══════════════════════════════════════════════════════════════════

export interface TelephonyConfig {
  enabled: boolean;
  browserCalling: boolean;
  callbackMode: boolean;
  callerId: string;
  userMobile: string;
  userLandline: string;
}

/** Angezeigte Rufnummer: feste KARS-Rufnummer oder eigene Geschäftsnummer. */
export type CallerIdMode = "kars" | "company";

export interface CallerOption {
  mode: CallerIdMode;
  number: string;
}

/** Auswahl für den Anruf-Dialog (Kundenfeedback 30.10.2026, I). */
export interface CallerOptions {
  options: CallerOption[];
  /** Vorbelegung: Favorit, sonst Vorgabe von Unternehmen bzw. Organisation. */
  preselected: CallerIdMode;
  defaultMode: CallerIdMode;
  /** Persönlicher Favorit; leer, wenn keiner gespeichert ist. */
  favorite: CallerIdMode | "";
  hasCompanyNumber: boolean;
  /** Unternehmen des Anrufs; null, wenn keines eindeutig ist. */
  idCompany: number | null;
}

export interface InitiateCallPayload {
  to: string;
  device?: "mobile" | "landline" | "browser";
  idCase?: number | null;
  idContact?: number | null;
  contactLabel?: string;
  /** Opt-in: record the call and run Whisper transcription + Claude summary via Replicate. */
  aiEnabled?: boolean;
  /** Anzeige-Nummer für diesen Anruf; ohne Angabe gilt die Vorgabe. */
  callerIdMode?: CallerIdMode;
}

export interface InitiateCallResponse {
  callLogId: number;
  twilioCallSid: string;
  mode: string;
  status: string;
}

export interface CallLogEntry {
  id: number;
  idOrganization: number;
  idUser: number;
  idCase?: number | null;
  idContact?: number | null;
  direction: "outbound" | "inbound";
  mode: "browser" | "callback" | "api";
  phoneFrom?: string | null;
  phoneTo?: string | null;
  contactLabel?: string | null;
  status: string;
  /** Twilio-Status des angerufenen Kontakts (zweites Bein im Callback-Modus). */
  calleeStatus?: string | null;
  durationSeconds?: number | null;
  recordingUrl?: string | null;
  twilioCallSid?: string | null;
  startedAt: string;
  answeredAt?: string | null;
  endedAt?: string | null;
  aiEnabled?: boolean;
  aiStatus?: string;
  aiTranscript?: string | null;
  aiSummary?: string | null;
  aiError?: string | null;
  aiCompletedAt?: string | null;
}

export interface VoiceTokenResponse {
  token: string;
  identity: string;
  ttl: number;
}

// ═══════════════════════════════════════════════════════════════════
// API
// ═══════════════════════════════════════════════════════════════════

export function getTelephonyConfig(): Promise<TelephonyConfig> {
  return getRequestGeneric("/protected/telephony/config");
}

export function initiateCall(payload: InitiateCallPayload): Promise<InitiateCallResponse> {
  // Use a telephony-specific error handler so the backend's validation
  // message (e.g. "Keine Telefonnummer im Benutzerprofil hinterlegt")
  // reaches the user instead of being replaced by the generic 400 text.
  return postRequest(
    "/protected/telephony/calls",
    payload,
    async (response) => {
      const data = await response.json().catch(() => ({}));
      const message =
        (data && (data.error || data.message)) ||
        "Anruf konnte nicht initiiert werden.";
      throw { message, errorCode: response.status };
    },
    true
  );
}

// Die Auswahl ist Komfort: Ein Fehler darf den Anruf nicht verhindern und weder
// abmelden noch eine Meldung zeigen, deshalb ohne die generische Fehlerbehandlung.
const callerOptionsError: ErrorHandler = async (response) => {
  throw new Error(`caller-options: HTTP ${response.status}`);
};

export function getCallerOptions(idCase?: number | null): Promise<CallerOptions> {
  const query = idCase ? `?idCase=${idCase}` : "";
  return getRequest(`/protected/telephony/caller-options${query}`, callerOptionsError, true);
}

export function saveCallerFavorite(favorite: CallerIdMode): Promise<{ favorite: CallerIdMode }> {
  return putRequest("/protected/telephony/caller-options", { favorite }, callerOptionsError, true);
}

export function listCalls(limit?: number): Promise<CallLogEntry[]> {
  const query = limit && limit > 0 ? `?limit=${Math.floor(limit)}` : "";
  return getRequestGeneric(`/protected/telephony/calls${query}`);
}

export function getVoiceToken(): Promise<VoiceTokenResponse> {
  return getRequestGeneric("/protected/telephony/token");
}
